<script setup lang="ts">
import { onKeyStroke } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';

import { getCssCustomPropertyValue } from '../../helpers/cssCustomProperties';
import { KeyboardKeyCode } from '../../models';
import type { AppTransitionTimeType } from '../transitions/model';

interface OverlayProps {
  isModal?: boolean;
  position?: 'fixed' | 'absolute';
  width?: string;
  height?: string;
  transitionTime?: AppTransitionTimeType;
}

const props = withDefaults(defineProps<OverlayProps>(), {
  position: 'fixed',
  height: '100dvh',
  width: '100vw',
  isModal: false,
});

const isActive = ref<boolean>(false);

export interface AppOverlayEmits {
  (eventName: 'close'): void;
}

const emit = defineEmits<AppOverlayEmits>();

const transitionTimeName = computed<string | undefined>(
  () => props.transitionTime && `transition-${props.transitionTime}`
);

const modalZIndex = getCssCustomPropertyValue('modal-overlay-z-index');
const defaultZIndex = '12';

onKeyStroke(KeyboardKeyCode.Escape, () => emit('close'));

onMounted(() => {
  isActive.value = true;
});
</script>

<template>
  <div
    :style="{
      position: props.position,
      width: props.width,
      height: props.height,
      'z-index': props.isModal ? modalZIndex : defaultZIndex,
    }"
    :class="[
      'app-overlay',
      transitionTimeName,
      {
        'is-modal': props.isModal,
        active: isActive,
      },
    ]"
  >
    <slot />
  </div>
</template>

<style lang="scss" scoped>
@use '../../styles/media' as *;

$durations: (
  very-short: var(--transition-very-short),
  short: var(--transition-short),
  medium: var(--transition-medium),
  long: var(--transition-long),
  very-long: var(--transition-very-long),
);

.app-overlay {
  all: unset;
  top: 0;
  left: 0;
  display: flex;
  background-color: var(--color-transpartent);
  overflow: auto;
  /* stylelint-disable-next-line declaration-property-value-keyword-no-deprecated */
  overflow: overlay;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-24);
  min-height: -webkit-fill-available;

  @each $time, $duration in $durations {
    &.transition-#{$time} {
      transition: all #{$duration};
    }
  }

  &.active {
    background-color: var(--color-overlay);
  }

  @include for-less-or-equal-than-900-px {
    padding: 0;
    background-color: var(--color-white);
    &.is-modal {
      background-color: var(--color-overlay);
    }
  }
}
</style>
